// Vencimientos (PP-23): lo vencido y lo que vence en los proximos
// EXPIRY_NOTICE_DAYS dias, en todas las plantas de la cuenta, con el vehiculo
// o conductor, las plantas afectadas y "Renovar" en la misma fila. El
// proveedor se entera aca, sin depender de su propia prolijidad ni de UCORP.
//
// Pensado como seccion de la barra del shell (PP-62): recibe la sesion, el
// puerto de la nomina y las plantas de la cuenta, y no conoce la navegacion.
//
// La renovacion reemplaza al documento vigente (PATCH, ADR-014 §2): el papel
// es uno solo y vale en todas las plantas que lo exigen, asi que se renueva
// una vez aunque la fila nombre varias plantas.
//
// Rediseño (docs/PLAN-REDISENO-PORTAL.md §6.7): dos paneles con filas densas,
// Renovar abre la hoja lateral con el formulario y confirma con un toast.
import { useId, useState } from "react";
import { EXPIRY_NOTICE_DAYS } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { Alert } from "../components/ui/alert.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { cn } from "../components/ui/cn.js";
import { EmptyState } from "../components/ui/empty.js";
import { Failure } from "../components/ui/failure.js";
import { Icon } from "../components/ui/icon.js";
import { Sheet } from "../components/ui/sheet.js";
import { SkeletonRows } from "../components/ui/skeleton.js";
import { useToast } from "../components/ui/toast.js";
import { DocumentForm, DocumentStatusBadge, documentErrorCopy } from "../fleet/documents/parts.js";
import { documentTypeLabel, expiryDetail, formatCalendarDate, renewalBody, type UploadDraft } from "../logic/documents.js";
import type { ExpiryEntry } from "../logic/expiries.js";
import type { SiteAccess } from "../sites/port.js";
import { useExpiries, type ExpiriesPort } from "./useExpiries.js";

interface Props {
  session: PortalSession;
  port: ExpiriesPort;
  /** Plantas de la cuenta (habilitaciones de la empresa madre, ADR-015). */
  sites: readonly SiteAccess[];
  /** Plantas leidas a la vez; por defecto EXPIRIES_CONCURRENCY. */
  concurrency?: number;
}

export function Expiries({ session, port, sites, concurrency }: Props): React.JSX.Element {
  const baseId = useId();
  const { notify } = useToast();
  const expiries = useExpiries(port, session, sites, concurrency);
  const { state } = expiries;
  const [renewing, setRenewing] = useState<string | null>(null);

  async function renew(entry: ExpiryEntry, draft: UploadDraft): Promise<string | undefined> {
    const body = renewalBody(draft);
    if (body === null) return "La fecha de vencimiento no es válida.";
    try {
      await port.updateDocument(session, entry.renewVia, entry.documentId, body);
      setRenewing(null);
      notify(`${documentTypeLabel(entry.documentType)} de ${subjectText(entry)} renovado.`, "ok");
      expiries.refresh();
      return undefined;
    } catch (error) {
      return documentErrorCopy(error);
    }
  }

  const groupProps = { renewing, onRenew: setRenewing, onCancel: () => setRenewing(null), onSubmit: renew };

  return (
    <section aria-labelledby={`${baseId}-title`} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 id={`${baseId}-title`} className="m-0 text-xl font-semibold leading-titulo tracking-titulo text-blanco">
            Vencimientos
          </h1>
          <p className="m-0 text-xs text-texto-suave">
            Documentos vencidos y los que vencen en los próximos {EXPIRY_NOTICE_DAYS} días, en todas las plantas de la empresa.
          </p>
        </div>
        <Button variant="secundario" size="sm" disabled={state.estado === "cargando"} onClick={expiries.reload}>
          <Icon name="actualizar" />
          Actualizar
        </Button>
      </div>

      {state.estado === "cargando" && <SkeletonRows rows={4} label="Consultando los documentos de cada planta" />}
      {state.estado === "denegado" && (
        <Failure title="La cuenta no tiene acceso a los documentos de la empresa.">Salir e ingresar con una cuenta de operador de proveedor.</Failure>
      )}
      {state.estado === "error" && <Failure title="No se pudieron consultar los vencimientos. Reintentar en unos minutos." onRetry={expiries.reload} />}

      {state.estado === "listo" && (
        <>
          {sites.length === 0 && <EmptyState icon="vencimientos" title="La empresa no está habilitada en ninguna planta." />}
          {state.failedSites.length > 0 && (
            <Alert variant="aviso">
              No se pudo consultar {state.failedSites.length === 1 ? "la planta" : "las plantas"} {state.failedSites.join(", ")}: la lista puede estar incompleta.
            </Alert>
          )}
          <ExpiryGroup
            id={`${baseId}-expired`}
            title="Vencidos"
            tone="critico"
            empty="No hay documentos vencidos."
            entries={state.expiries.expired}
            {...groupProps}
          />
          <ExpiryGroup
            id={`${baseId}-soon`}
            title={`Vencen en los próximos ${String(EXPIRY_NOTICE_DAYS)} días`}
            tone="ambar"
            empty={`Ningún documento vence en los próximos ${String(EXPIRY_NOTICE_DAYS)} días.`}
            entries={state.expiries.expiringSoon}
            {...groupProps}
          />
        </>
      )}
    </section>
  );
}

function subjectText(entry: ExpiryEntry): string {
  return `${entry.subject.type === "vehicle" ? "Vehículo" : "Conductor"} ${entry.subject.code} · ${entry.subject.detail}`;
}

function ExpiryGroup({
  id,
  title,
  tone,
  empty,
  entries,
  renewing,
  onRenew,
  onCancel,
  onSubmit,
}: {
  id: string;
  title: string;
  tone: "critico" | "ambar";
  empty: string;
  entries: readonly ExpiryEntry[];
  renewing: string | null;
  onRenew: (documentId: string) => void;
  onCancel: () => void;
  onSubmit: (entry: ExpiryEntry, draft: UploadDraft) => Promise<string | undefined>;
}): React.JSX.Element {
  return (
    <Card role="region" aria-labelledby={id} className="gap-0 p-0">
      {/* El contador va dentro del titulo: el nombre de la region es "Vencidos (3)". */}
      <div className="flex min-h-10 items-center gap-2 border-b border-divisor px-3.5 py-2">
        <i aria-hidden="true" className={cn("inline-block size-2 rounded-full", tone === "critico" ? "bg-critico" : "bg-ambar")} />
        <h2 id={id} className="rotulo m-0 text-texto-suave">
          {title} <span className="text-texto">({entries.length})</span>
        </h2>
      </div>
      {entries.length === 0 ? (
        <p role="status" className="m-0 px-3.5 py-3 text-sm text-texto-suave">
          {empty}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col p-0">
          {entries.map((entry) => {
            const label = documentTypeLabel(entry.documentType);
            const subject = subjectText(entry);
            return (
              <li
                key={entry.documentId}
                aria-label={`${label} de ${subject}`}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-t border-divisor px-3.5 py-2 first:border-t-0 hover:bg-velo md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:min-h-fila md:py-1"
              >
                <span className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-texto">{label}</span>
                  <DocumentStatusBadge status={entry.status} />
                </span>
                <span className="col-span-2 text-sm text-texto-2 md:col-span-1">{subject}</span>
                <span className="col-span-2 flex min-w-0 flex-col text-xs text-texto-suave md:col-span-1">
                  <span className="tabular">
                    {entry.expiresAt === null ? "No vence" : `${formatCalendarDate(entry.expiresAt)}, ${expiryDetail(entry.expiresAt, entry.today)}`}
                  </span>
                  <span className="truncate">
                    {entry.sites.length === 1 ? "Planta afectada: " : "Plantas afectadas: "}
                    {entry.sites.map((s) => s.name).join(", ")}
                  </span>
                </span>
                <span className="col-start-2 row-start-1 md:col-start-auto md:row-start-auto">
                  <Button
                    size="sm"
                    variant={tone === "critico" ? "primario" : "secundario"}
                    aria-label={`Renovar ${label} de ${subject}`}
                    disabled={renewing !== null}
                    onClick={() => onRenew(entry.documentId)}
                  >
                    Renovar
                  </Button>
                </span>
                <Sheet open={renewing === entry.documentId} onClose={onCancel} title={`Renovar ${label}`} subtitle={subject}>
                  {renewing === entry.documentId && (
                    <DocumentForm mode="renew" type={entry.documentType} subjectLabel={subject} onSubmit={(draft) => onSubmit(entry, draft)} onCancel={onCancel} />
                  )}
                </Sheet>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
